import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { WorkspaceProvider } from '@/contexts/WorkspaceContext';
import { ToastProvider } from '@/components/Toast';
import ProtectedRoute from '@/components/ProtectedRoute';
import AppShell from '@/components/AppShell';
import CookieBanner from '@/components/CookieBanner';
import MarkLoader from '@/components/MarkLoader';
import { FEATURES } from '@/lib/featureFlags';
import { isBetaEnabled } from '@/v2/featureFlags';

// Route-based code splitting — each page becomes its own webpack chunk.
// @react-pdf/renderer (~500KB) lands in the faktury/reporting chunks only,
// keeping the Dashboard/Nastavenia LCP path lean.
const Login = lazy(() => import('@/pages/Login'));
const AuthCallback = lazy(() => import('@/pages/AuthCallback'));
const Onboarding = lazy(() => import('@/pages/Onboarding'));
const OnboardingV2 = lazy(() => import('@/v2/onboarding/OnboardingContainer'));
const KlientDetail = lazy(() => import('@/pages/KlientDetail'));
const Vydavky = lazy(() => import('@/pages/Vydavky'));
const Dokumenty = lazy(() => import('@/pages/Dokumenty'));
const Nastavenia = lazy(() => import('@/pages/Nastavenia'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Terms = lazy(() => import('@/pages/Terms'));
const Design = lazy(() => import('@/pages/Design'));
const TemplateCheck = lazy(() => import('@/pages/TemplateCheck'));
const IconsPreview = lazy(() => import('@/pages/IconsPreview'));
const Reporting = lazy(() => import('@/pages/Reporting'));
const Polozky = lazy(() => import('@/pages/Polozky'));
const InvoicePrint = lazy(() => import('@/pages/InvoicePrint'));
const NewInvoiceScreen = lazy(() => import('@/components/modals/NewInvoiceScreen'));
const CenovePonuky = lazy(() => import('@/pages/CenovePonuky'));
const Zakazky = lazy(() => import('@/pages/Zakazky'));
const NovaZakazka = lazy(() => import('@/pages/NovaZakazka'));
const ZakazkaDetail = lazy(() => import('@/pages/ZakazkaDetail'));
const QuoteEditorScreen = lazy(() => import('@/components/modals/QuoteEditorScreen'));
const QuotePrint = lazy(() => import('@/pages/QuotePrint'));

// TODUE V2 M0 — vertikálny rez (local-first, oddelený od Supabase, AC24).
const ExpenseListV2 = lazy(() => import('@/pages/ExpenseListV2'));
const AddExpenseV2 = lazy(() => import('@/pages/AddExpenseV2'));
const DashboardV2 = lazy(() => import('@/pages/DashboardV2'));
const FinanceBoardV2 = lazy(() => import('@/pages/FinanceBoardV2'));
const AskNumbersV2 = lazy(() => import('@/pages/AskNumbersV2'));
const InvoiceListV2 = lazy(() => import('@/pages/InvoiceListV2'));
const ReportingV2 = lazy(() => import('@/pages/ReportingV2'));
const CounterpartiesV2 = lazy(() => import('@/pages/CounterpartiesV2'));
const KlientDetailV2 = lazy(() => import('@/pages/KlientDetailV2'));
const SettingsV2 = lazy(() => import('@/pages/SettingsV2'));
const DocInboxV2 = lazy(() => import('@/pages/DocInboxV2'));
const SkladScreen = lazy(() => import('@/pages/SkladScreen'));
const DochadzkaScreen = lazy(() => import('@/pages/DochadzkaScreen'));
const ProjektyScreen = lazy(() => import('@/pages/ProjektyScreen'));
const PodujatiaScreen = lazy(() => import('@/pages/PodujatiaScreen'));
const PonukyScreen = lazy(() => import('@/pages/PonukyScreen'));
const PrenajomScreen = lazy(() => import('@/pages/PrenajomScreen'));
const PredplatneScreen = lazy(() => import('@/pages/PredplatneScreen'));
const KusovnikScreen = lazy(() => import('@/pages/KusovnikScreen'));
const KnihaJazdScreen = lazy(() => import('@/pages/KnihaJazdScreen'));
const SearchV2 = lazy(() => import('@/pages/SearchV2'));
const PoradcaV2 = lazy(() => import('@/pages/PoradcaV2'));
// V2 app shell — ľavé menu okolo /v2/* obrazoviek (public, bez Supabase, AC24).
const V2Shell = lazy(() => import('@/v2/V2Shell'));
// Auth + povinný Drive backup gate — brána pred V2Shell, vynucuje pripojenie Drive.
const AuthBackupGate = lazy(() => import('@/v2/AuthBackupGate'));
// DEV sandbox — živé ťukanie do schedule engine (NS-005) + migrácií (NS-006). Len development.
const DevSandboxV2 = lazy(() => import('@/pages/DevSandboxV2'));
// DEV preview — TOD asistent + Dielňa vizuálny brief pre Jožka, klikateľný náčrt. Len development.
const TodPreviewV2 = lazy(() => import('@/pages/TodPreviewV2'));
const CollaboratorTask = lazy(() => import('@/pages/CollaboratorTask'));
const SchvalenieKlienta = lazy(() => import('@/pages/SchvalenieKlienta'));

const COOKIE_HIDE_PATHS = ['/login', '/auth/callback', '/privacy', '/terms'];

function BannerHost() {
  const location = useLocation();
  if (COOKIE_HIDE_PATHS.includes(location.pathname)) return null;
  return <CookieBanner />;
}

// V2 onboarding route — container nezávisí od routera, navigáciu po dokončení dodáme tu.
function V2OnboardingRoute() {
  const navigate = useNavigate();
  return <OnboardingV2 onDone={() => navigate('/prehlad')} />;
}

function RouteFallback() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
      <MarkLoader variant="pulse" size={32} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <WorkspaceProvider>
          <ToastProvider>
            <Suspense fallback={<RouteFallback />}>
              <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/design" element={<Design />} />
              <Route path="/design/templates" element={<TemplateCheck />} />
              <Route path="/icons" element={<IconsPreview />} />
              {/* TOD asistent + Dielňa — klikateľný vizuálny brief pre Jožka, žiadna Drive/auth
                  brána (rovnaký precedens ako /design a /icons — čistá UI preview, nulové dáta).
                  Len development, viď docs/handoffs/HANDOFF-jozko-2026-08-06-tod-asistent-dielna-vizual.md. */}
              {process.env.NODE_ENV !== 'production' && <Route path="/v2/dev/tod" element={<TodPreviewV2 />} />}
              <Route path="/c/" element={<CollaboratorTask />} />
              {/* Schválenie ponuky klientom — CUDZIA obrazovka. Verejná, bez prihlásenia:
                  klient todue nepozná a účet nemá. Kľúč je vo fragmente (/s/#token),
                  ktorý sa na server neodosiela. */}
              <Route path="/s/" element={<SchvalenieKlienta />} />

              {/* TODUE V2 — VŠETKY /v2 obrazovky (shell aj full-screen formulár/onboarding/dev)
                  idú cez AuthBackupGate ako layout route (Outlet) — brána nesmie mať sirotu.
                  Cross-family verify (Codex, 2026-08-05) chytil: full-screen cesty nižšie boli
                  pôvodne MIMO brány → priame otvorenie /v2/vydavky/novy obišlo povinnú zálohu.
                  V2Shell je PUBLIC layout bez Supabase/workspace couplingu (AC24) — len
                  navigácia + Outlet. Beta moduly (sklad/dochádzka/projekty/hľadať) žijú LEN za
                  v2:beta flagom (app2/beta ON), každý má vlastný guard. */}
              <Route element={<AuthBackupGate><Outlet /></AuthBackupGate>}>
                <Route element={<V2Shell />}>
                  <Route path="/prehlad" element={<DashboardV2 />} />
                  <Route path="/financie" element={<FinanceBoardV2 />} />
                  {/* /v2/ prefix na Výdavky/Reporting/Nastavenia ZÁMERNÝ (vrátené 2026-08-07,
                      cross-family review Sol/Codex): bare cesty ešte drží V1, ktorý má funkcie
                      V2 náhrada ešte nemá — hromadný import výdavkov (Vydavky.jsx), bankový CSV
                      import (Reporting), správa účtu/súkromia/IBAN pre V1 faktúry (Nastavenia,
                      CookieBanner "Spravovať" naň linkuje). Prvý pokus tieto retirol — pozri
                      HANDOFF/BUILD-JOURNAL 2026-08-07 pre plný zoznam nájdených regresií. */}
                  <Route path="/v2/vydavky" element={<ExpenseListV2 />} />
                  <Route path="/cisla" element={<AskNumbersV2 />} />
                  <Route path="/faktury" element={<InvoiceListV2 />} />
                  <Route path="/klienti" element={<CounterpartiesV2 />} />
                  {/* Detail klienta ZÁMERNE ostáva na /v2/ prefixe — "/klienti/:id" bez prefixu
                      je už obsadené V1 KlientDetail (interné detail flowy, viď komentár nižšie).
                      Zdroj pravdy pre ktorý sa použije: SidebarNav pinned klienti (V2Shell) linkujú
                      sem explicitne, V1 odkazy na klienta idú na "/klienti/:id". */}
                  <Route path="/v2/klienti/:id" element={<KlientDetailV2 />} />
                  <Route path="/v2/nastavenia" element={<SettingsV2 />} />
                  <Route path="/doklady" element={<DocInboxV2 />} />
                  <Route path="/v2/reporting" element={<ReportingV2 />} />
                  {isBetaEnabled() && <Route path="/sklad" element={<SkladScreen />} />}
                  {isBetaEnabled() && <Route path="/dochadzka" element={<DochadzkaScreen />} />}
                  {isBetaEnabled() && <Route path="/projekty" element={<ProjektyScreen />} />}
                  {isBetaEnabled() && <Route path="/podujatia" element={<PodujatiaScreen />} />}
                  {isBetaEnabled() && <Route path="/ponuky" element={<PonukyScreen />} />}
                  {isBetaEnabled() && <Route path="/prenajom" element={<PrenajomScreen />} />}
                  {isBetaEnabled() && <Route path="/predplatne" element={<PredplatneScreen />} />}
                  {isBetaEnabled() && <Route path="/kusovnik" element={<KusovnikScreen />} />}
                  {isBetaEnabled() && <Route path="/kniha-jazd" element={<KnihaJazdScreen />} />}
                  {isBetaEnabled() && <Route path="/hladat" element={<SearchV2 />} />}
                  {isBetaEnabled() && <Route path="/poradca" element={<PoradcaV2 />} />}
                </Route>

                {/* Full-screen V2 (bez menu) — formulár, onboarding, dev sandbox. Teraz POD bránou. */}
                <Route path="/vydavky/novy" element={<AddExpenseV2 />} />
                <Route path="/v2/onboarding" element={<V2OnboardingRoute />} />
                {process.env.NODE_ENV !== 'production' && <Route path="/v2/dev" element={<DevSandboxV2 />} />}
              </Route>
              <Route path="/faktury/:id/print-html" element={<ProtectedRoute><InvoicePrint /></ProtectedRoute>} />
              <Route path="/cenove-ponuky/:id/print" element={<ProtectedRoute><QuotePrint /></ProtectedRoute>} />

              {/* Onboarding — protected (must be logged in) but no workspace required */}
              <Route path="/onboarding" element={<ProtectedRoute requireWorkspace={false}><Onboarding /></ProtectedRoute>} />

              {/* Main app shell — protected + requires workspace */}
              <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
                {/* Root → V2 (rovnaký vzor ako /faktury a /klienti nižšie). Predtým renderoval
                    starú V1 <Dashboard/> — jediný neopravený vstupný bod do V1, keďže V2 je
                    jediný produkčný vstup (viď komentár nižšie). Namerané 2026-08-07: po
                    app.todue.sk cutoveri (predtým čisto V1 projekt todue-app, teraz zdieľaný
                    build s V2) sa cez "/" aj catch-all (riadok nižšie, "*"→"/") dalo omylom
                    dostať na starý dizajn uprostred V2 session. */}
                <Route path="/" element={<Navigate to="/prehlad" replace />} />
                {/* Faktúry zoznam — "/faktury" JE teraz priamo V2Shell routa (vyššie), starý
                    presmerovací duplikát odstránený. "/faktury/nova" NIE JE alias "/faktury" v
                    react-router (samostatná cesta) — musí zostať zaregistrovaná, používajú ju
                    Onboarding.jsx, KlientDetail.jsx (3×), MobileTopbar.jsx. Cross-family review
                    (Sol/Codex, 2026-08-07) chytil že prvý pokus ju omylom zmazal ako "duplicitnú".
                    /faktury/:id/upravit ostáva V1 kvôli interným flowom (konverzia ponuky,
                    recurring, edit). */}
                <Route path="/faktury/nova" element={<Navigate to="/faktury" replace />} />
                <Route path="/faktury/:id/upravit" element={<NewInvoiceScreen />} />
                <Route path="/cenove-ponuky" element={<CenovePonuky />} />
                <Route path="/cenove-ponuky/nova" element={<QuoteEditorScreen />} />
                <Route path="/cenove-ponuky/:id/upravit" element={<QuoteEditorScreen />} />
                {/* Klienti zoznam — "/klienti" JE teraz priamo V2Shell routa (starý presmerovací
                    duplikát odstránený, rovnaký dôvod ako Faktúry vyššie).
                    /klienti/:id ostáva V1 kvôli interným detail flowom (V2 detail žije na
                    /v2/klienti/:id, samostatná routa vyššie). */}
                <Route path="/klienti/:id" element={<KlientDetail />} />
                <Route path="/vydavky" element={<Vydavky />} />
                {/* Zákazky (Dielňa) — za V2 beta flagom, rovnako ako sekcia v menu.
                    Priamy vstup na URL pri vypnutom flagu padne na catch-all → "/". */}
                {FEATURES.zakazky && <Route path="/zakazky" element={<Zakazky />} />}
                {FEATURES.zakazky && <Route path="/zakazky/nova" element={<NovaZakazka />} />}
                {FEATURES.zakazky && <Route path="/zakazky/:id" element={<ZakazkaDetail />} />}
                <Route path="/polozky" element={<Polozky />} />
                {FEATURES.documents && <Route path="/dokumenty" element={<Dokumenty />} />}
                <Route path="/reporting" element={<Reporting />} />
                <Route path="/nastavenia" element={<Nastavenia />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
            <BannerHost />
          </ToastProvider>
        </WorkspaceProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
